import React, { useMemo } from 'react';

import Image from 'components/uiLibrary/Image';
import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';

import CommonTrans from 'components/Globals/CommonTrans';

import { TP, OUTBOUND_LINK_TYPES } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';

import { ROUTE_VALIDITY } from 'operabase-router/constants';

import LogoImage from 'public/images/logo.png';

import classes from './ErrorContent.module.scss';

const HomePageLogo = ({ skipLinkToHome = false }) => {
  if (skipLinkToHome) {
    return (
      <Image
        src={LogoImage}
        alt="Operabase Home"
        lazy={false}
        className={classes.logo}
        disableCloudinary
        disableNextImage
      />
    );
  }

  return (
    <LinkButton variant="native" href="/[[..action]]" as="/" isLink>
      <HomePageLogo skipLinkToHome />
    </LinkButton>
  );
};

const ErrorContent = ({ title, description, footer, errorId, skipLinkToHome = false }) => {
  const { obRouteContext } = useAppContext();

  const hasRouteContext = useMemo(() => Object.keys(obRouteContext || {}).length > 0, [obRouteContext]);
  const pageURL = useMemo(
    () => (typeof window === 'undefined' ? obRouteContext?.url : window.location.href),
    [obRouteContext],
  );

  const reasons = useMemo(() => {
    const showDebugInfo = hasRouteContext && process.env.APP_ENV && process.env.APP_ENV !== 'production';

    if (showDebugInfo) {
      return obRouteContext?.paths
        ?.reduce((acc, path) => {
          if (path.validity === ROUTE_VALIDITY.VALID) {
            return acc;
          }

          return [...acc, `${path.path} (${path.reason})`];
        }, [])
        .join(', ');
    }

    return null;
  }, [hasRouteContext, obRouteContext]);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <HomePageLogo skipLinkToHome={skipLinkToHome} />
        <div className={classes.content}>
          <Typography variant="h1" weight="medium" size="18">
            {title}
          </Typography>
          <Typography variant="h2" size="16">
            {description}
          </Typography>
        </div>
        {footer}
        <div className={classes.content}>
          <Typography variant="p" weight="regular" size="12">
            <CommonTrans
              ns="NS_APP_GLOBALS"
              i18nKey={`${TP}.FN_CONTACT_US_AT_EMAIL`}
              components={{
                email: (
                  <TextButton
                    href="mailto:contact@operabase.com"
                    isLink
                    external
                    externalLinkType={OUTBOUND_LINK_TYPES.EMAIL}
                  />
                ),
              }}
            />
          </Typography>
        </div>
        <div className={classes.debug}>
          <Typography variant="p" weight="regular" size="10">
            <Typography weight="bold" size="10">
              URL:{' '}
            </Typography>
            <Typography weight="regular" size="10">
              {pageURL}
            </Typography>
          </Typography>
          <Typography variant="p" weight="regular" size="10">
            <Typography weight="bold" size="10">
              BUILD:{' '}
            </Typography>
            <Typography weight="regular" size="10">
              {process.env.BUILD_NUMBER}
            </Typography>
          </Typography>
          {hasRouteContext && (
            <>
              {obRouteContext.tokenized && (
                <Typography variant="p" weight="regular" size="10">
                  <Typography weight="bold" size="10">
                    TOKEN:{' '}
                  </Typography>
                  <Typography weight="regular" size="10">
                    {obRouteContext.tokenized}
                  </Typography>
                </Typography>
              )}
              <Typography variant="p" weight="regular" size="10">
                <Typography weight="bold" size="10">
                  STATUS:{' '}
                </Typography>
                <Typography weight="regular" size="10">
                  {obRouteContext.status}
                </Typography>
              </Typography>
              {obRouteContext.redirect && (
                <Typography variant="p" weight="regular" size="10">
                  <Typography weight="bold" size="10">
                    REDIRECT:{' '}
                  </Typography>
                  <Typography weight="regular" size="10">
                    {obRouteContext.redirect}
                  </Typography>
                </Typography>
              )}
            </>
          )}
          {reasons && (
            <Typography variant="p" weight="regular" size="10">
              <Typography weight="bold" size="10">
                REASONS:{' '}
              </Typography>
              <Typography weight="regular" size="10">
                {reasons}
              </Typography>
            </Typography>
          )}
          {errorId && (
            <Typography variant="p" weight="regular" size="10">
              <Typography weight="bold" size="10">
                ERROR ID:{' '}
              </Typography>
              <Typography weight="regular" size="10">
                {errorId}
              </Typography>
            </Typography>
          )}
          {(obRouteContext.identity?.visitorId || obRouteContext.identity?.requestId) && (
            <Typography variant="p" weight="regular" size="10">
              <Typography weight="bold" size="10">
                TRACE ID:{' '}
              </Typography>
              <Typography weight="regular" size="10">
                {obRouteContext.identity?.visitorId}/{obRouteContext.identity?.requestId}
              </Typography>
            </Typography>
          )}
          <Typography variant="p" weight="regular" size="10">
            <Typography weight="bold" size="10">
              TIMESTAMP:{' '}
            </Typography>
            <Typography weight="regular" size="10">
              {new Date().toISOString()}
            </Typography>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default ErrorContent;
